/**
 * Account tags on the nomination form: a multi-select over a fixed list, pre-filled from the picked
 * contact's SFDC account and editable by the BD.
 *
 * The list is the product's (ABM, T2K, DEEP, GREENFIELD, OTHERS — the last added for a customer none
 * of the others fits, Event Hub Testing doc #49). Customer Link spells some of them
 * differently ("Deep"), so a contact's tag is matched to an option case-insensitively and takes the
 * option's spelling. A tag that matches no option is kept as-is and offered as an extra option —
 * Customer Link may add tags, and dropping one silently would lose data the BD did not choose to
 * remove.
 *
 * Pre-fill follows the same rule as the other auto-filled fields (contactAutofill): the tags follow
 * the current pick while the BD has not changed them, and are left alone once they have.
 *
 * Here rather than in the page because the coverage config measures only `.ts`.
 */

export const ACCOUNT_TAG_OPTIONS = ['ABM', 'T2K', 'DEEP', 'GREENFIELD', 'OTHERS'] as const;

/** A tag in the form's spelling: the matching option's, or as given when no option matches. */
export function canonicalTag(tag: string): string {
  const t = tag.trim();
  return ACCOUNT_TAG_OPTIONS.find((o) => o.toLowerCase() === t.toLowerCase()) ?? t;
}

/** A contact's tags in the form's spelling, blanks and duplicates dropped, order kept. */
export function canonicalTags(tags: readonly string[] | undefined): string[] {
  const out: string[] = [];
  for (const tag of tags ?? []) {
    const c = canonicalTag(tag);
    if (c && !out.includes(c)) out.push(c);
  }
  return out;
}

/** The options to show: the fixed list, then any selected tag that is not on it. */
export function tagOptions(selected: readonly string[]): string[] {
  const extra = selected.filter((t) => !(ACCOUNT_TAG_OPTIONS as readonly string[]).includes(t));
  return [...ACCOUNT_TAG_OPTIONS, ...extra];
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((t) => b.includes(t));
}

/**
 * The tags after a (re)pick.
 *
 * @param current  what is selected now
 * @param filled   what the last pick put there
 * @param next     the new contact's tags (canonical); `[]` for no contact
 * @returns the new selection, and what this pick filled (to pass as `filled` next time)
 */
export function applyTagFill(
  current: readonly string[],
  filled: readonly string[],
  next: readonly string[],
): { tags: string[]; filled: string[] } {
  const untouched = current.length === 0 || sameSet(current, filled);
  return untouched ? { tags: [...next], filled: [...next] } : { tags: [...current], filled: [...filled] };
}
